import { clearStoredSession, getStoredToken, setStoredSession } from './platformSession';

const API_BASE = '/api';

async function apiCall(method, path, body = null, requireAuth = true) {
  const headers = { 'Content-Type': 'application/json' };
  if (requireAuth) {
    const token = getStoredToken();
    if (!token) throw new Error('Sesión no disponible');
    headers.Authorization = `Bearer ${token}`;
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 12000);

  let response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw new Error('La solicitud tardó demasiado. Verifique API, base de datos o proxy del servidor.');
    }
    throw error;
  } finally {
    window.clearTimeout(timeoutId);
  }

  let data = {};
  try {
    data = await response.json();
  } catch {
    data = {};
  }

  if (!response.ok) {
    if (response.status === 401) clearStoredSession();
    throw new Error(data.error || `HTTP ${response.status}`);
  }

  return data;
}

export async function login(email, password) {
  const data = await apiCall('POST', '/auth/login', { email, password }, false);
  setStoredSession(data.token, data.user);
  return data;
}

export async function getMe() {
  return apiCall('GET', '/auth/me');
}

export async function acceptPolicy(policyType) {
  return apiCall('POST', '/auth/policy-accept', { policyType });
}

export async function getUsers(role) {
  const query = role ? `?role=${encodeURIComponent(role)}` : '';
  return apiCall('GET', `/users${query}`);
}

export async function getSchemes() {
  return apiCall('GET', '/schemes');
}

export async function createUser(data) {
  return apiCall('POST', '/users', data);
}

export async function updateUser(id, data) {
  return apiCall('PUT', `/users/${id}`, data);
}

export async function toggleUserStatus(id) {
  return apiCall('PATCH', `/users/${id}/toggle-status`);
}

export async function resetUserPassword(id, password) {
  return apiCall('PATCH', `/users/${id}/password`, { password });
}

export async function createExam(data) {
  return apiCall('POST', '/exams', data);
}

export async function assignExam(data) {
  return apiCall('POST', '/exams/assignments', data);
}

export async function getAssignments() {
  return apiCall('GET', '/exams/assignments/list');
}

export async function getResults(candidateId) {
  const query = candidateId ? `?candidateId=${encodeURIComponent(candidateId)}` : '';
  return apiCall('GET', `/results${query}`);
}

export async function getResultDetail(id) {
  return apiCall('GET', `/results/${id}/detail`);
}

export async function gradeResult(id, data) {
  return apiCall('PUT', `/results/${id}/grade`, data);
}

export async function getBankQuestions(scheme, apt = null, type = null, search = null) {
  const params = new URLSearchParams();
  if (scheme) params.set('scheme', scheme);
  if (apt) params.set('apt', apt);
  if (type) params.set('type', type);
  if (search) params.set('search', search);
  const query = params.toString();
  return apiCall('GET', `/exams/bank/questions${query ? `?${query}` : ''}`);
}

export async function getBankApts(scheme, type = null) {
  const params = new URLSearchParams();
  if (scheme) params.set('scheme', scheme);
  if (type) params.set('type', type);
  const query = params.toString();
  return apiCall('GET', `/exams/bank/apts${query ? `?${query}` : ''}`);
}

export async function getBankCatalog(scheme) {
  const params = new URLSearchParams();
  if (scheme) params.set('scheme', scheme);
  const query = params.toString();
  return apiCall('GET', `/exams/bank/catalog${query ? `?${query}` : ''}`);
}

export async function createBankQuestion(data) {
  return apiCall('POST', '/exams/bank', data);
}

export async function updateBankQuestion(id, data, type = null) {
  const query = type ? `?type=${encodeURIComponent(type)}` : '';
  return apiCall('PUT', `/exams/bank/${id}${query}`, data);
}

export async function deleteBankQuestion(id, type = null) {
  const query = type ? `?type=${encodeURIComponent(type)}` : '';
  return apiCall('DELETE', `/exams/bank/${id}${query}`);
}

export async function importBankWorkbook({ file, type, expectedScheme = '' }) {
  const token = getStoredToken();
  if (!token) throw new Error('Sesión no disponible');

  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', type);
  if (expectedScheme) formData.append('expectedScheme', expectedScheme);

  const response = await fetch(`${API_BASE}/exams/bank/import`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  let data = {};
  try {
    data = await response.json();
  } catch {
    data = {};
  }

  if (!response.ok) {
    if (response.status === 401) clearStoredSession();
    throw new Error(data.error || `HTTP ${response.status}`);
  }

  return data;
}

export async function generateExamSelection({ scheme, omCount = 0, openCount = 0, prCount = 0 }) {
  const params = new URLSearchParams();
  if (scheme) params.set('scheme', scheme);
  params.set('omCount', String(omCount));
  params.set('openCount', String(openCount));
  params.set('prCount', String(prCount));
  return apiCall('GET', `/exams/bank/generate?${params.toString()}`);
}
